import { isApiError } from "@/lib/apiClient";
import i18n from "@/lib/i18n";
import { toast } from "sonner";

export type ApiValidationIssue = {
	path: (string | number)[];
	message: string;
	code?: string;
};

export type ApiValidationError = {
	error: "ValidationError";
	issues: ApiValidationIssue[];
};

export const isValidationError = (
	data: unknown,
): data is ApiValidationError => {
	if (!data || typeof data !== "object") return false;
	const d = data as { error?: unknown; issues?: unknown };
	return d.error === "ValidationError" && Array.isArray(d.issues);
};

export const extractValidationError = (
	err: unknown,
): ApiValidationError | null => {
	if (!isApiError(err)) return null;
	const data = err.response?.data;
	if (isValidationError(data)) return data;
	return null;
};

const fieldLabel = (field: string | number | undefined): string => {
	if (field === undefined) return i18n.t("apiErrors.fields.default");
	if (typeof field === "number") {
		return i18n.t("apiErrors.fields.entry", { index: field + 1 });
	}
	return i18n.t(`apiErrors.fields.${field}`, { defaultValue: `“${field}”` });
};

const translateMessage = (issue: ApiValidationIssue): string => {
	const last = issue.path[issue.path.length - 1];
	const field = fieldLabel(last);
	const msg = issue.message.toLowerCase();

	if (issue.code === "invalid_type" || /expected .*, received/.test(msg)) {
		return i18n.t("apiErrors.validation.invalid", { field });
	}
	if (issue.code === "too_small" || msg.includes("at least") || msg.includes("required")) {
		if (last === "rewards" || last === "outputs" || last === "inputs" || last === "entries" || last === "recipes" || last === "contentList" || last === "damage" || last === "onTrue" || last === "actions") {
			return i18n.t("apiErrors.validation.atLeastOne", { field });
		}
		return i18n.t("apiErrors.validation.required", { field });
	}
	if (issue.code === "too_big") {
		return i18n.t("apiErrors.validation.tooBig", { field });
	}
	if (msg.includes("must be > 0") || msg.includes("must be greater than 0")) {
		return i18n.t("apiErrors.validation.greaterThanZero", { field });
	}
	if (msg.includes("must be >= 0") || msg.includes("non-negative") || msg.includes("nonnegative")) {
		return i18n.t("apiErrors.validation.nonNegative", { field });
	}
	if (msg.includes("must be") && msg.includes("number")) {
		return i18n.t("apiErrors.validation.number", { field });
	}
	if (msg.includes("invalid enum") || msg.includes("invalid_enum_value")) {
		return i18n.t("apiErrors.validation.enum", { field });
	}

	return i18n.t("apiErrors.validation.other", {
		field,
		message: issue.message,
	});
};

export const toastApiError = (err: unknown, fallback: string): void => {
	if (isApiError(err) && err.response?.status === 413) {
		toast.error(i18n.t("apiErrors.bodyTooLarge"));
		return;
	}
	const validation = extractValidationError(err);
	if (validation) {
		const first = validation.issues[0];
		const count = validation.issues.length;
		const suffix = count > 1
			? i18n.t("apiErrors.validation.more", { count: count - 1 })
			: "";
		const title = first ? translateMessage(first) : fallback;
		toast.error(`${title}${suffix}`);
		return;
	}
	if (isApiError(err)) {
		const data = err.response?.data as { error?: unknown } | undefined;
		if (data && typeof data.error === "string" && data.error !== "ValidationError") {
			toast.error(errorCodeToMessage(data.error));
			return;
		}
	}
	toast.error(fallback);
};

function errorCodeToMessage(code: string): string {
	switch (code) {
		case "birthdate_required":
			return i18n.t("apiErrors.birthdateRequired");
		case "birthdate_invalid":
		case "birthdate_future":
		case "birthdate_too_old":
			return i18n.t("apiErrors.birthdateInvalid");
		case "parent_required":
			return i18n.t("apiErrors.parentRequired");
		case "checkout_requires_adult":
			return i18n.t("apiErrors.checkoutRequiresAdult");
		case "password_too_short":
			return i18n.t("apiErrors.passwordTooShort");
		case "password_needs_letter_and_number":
			return i18n.t("apiErrors.passwordNeedsLetterAndNumber");
		case "password_too_common":
			return i18n.t("apiErrors.passwordTooCommon");
		case "BODY_TOO_LARGE":
			return i18n.t("apiErrors.bodyTooLarge");
		default:
			return code;
	}
}
